import { useEffect, useState } from "react";
import { Wallet, Orders, Public } from "@/lib/api";
import { navigate } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Coins, Sparkles, AlertCircle, CreditCard } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { CheckinPanel } from "@/components/CheckinPanel";
import { RedeemPanel } from "@/components/RedeemPanel";
import { toast } from "@/components/ui/dialog";

type Provider = "stripe" | "alipay" | "wechat";

const PROVIDER_META: Record<Provider, { label: string; tone: string }> = {
  stripe: { label: "Stripe / 信用卡", tone: "bg-indigo-600 hover:bg-indigo-700 text-white" },
  alipay: { label: "支付宝",          tone: "bg-sky-600 hover:bg-sky-700 text-white" },
  wechat: { label: "微信支付",         tone: "bg-emerald-600 hover:bg-emerald-700 text-white" },
};

function fenToYuan(c: number) { return (c / 100).toFixed(2); }

interface Pkg {
  id: number; code: string; name: string; description?: string;
  points: number; bonus: number;
  price_cents: number; currency: string;
}

export function WalletPage() {
  const [w, setW] = useState<{ points: number } | null>(null);
  const [packages, setPackages] = useState<Pkg[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [tx, setTx] = useState<any[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [purchases, setPurchases] = useState<any[]>([]);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [pendingQR, setPendingQR] = useState<{ url: string; orderId: string } | null>(null);
  const PAGE_SIZE = 10;

  useEffect(() => {
    Wallet.get().then(setW).catch((e: any) => {
      if (e?.status === 401) navigate("/login");
      else setErr(e?.body?.message || "加载失败");
    });
    Wallet.packages().then(setPackages).catch(() => {});
    Public.siteConfig().then((site: any) => {
      const cfg = site?.["payment.providers"] || {};
      const enabled: Provider[] = (Object.keys(cfg) as Provider[]).filter(
        (k) => cfg[k] && cfg[k].enabled !== false && (k === "stripe" || k === "alipay" || k === "wechat"),
      );
      setProviders(enabled);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Wallet.transactions(txPage, PAGE_SIZE).then((r) => { setTx(r.items); setTxTotal(r.total); }).catch(() => {});
  }, [txPage]);

  useEffect(() => {
    Wallet.purchases(purchasesPage, PAGE_SIZE).then((r) => { setPurchases(r.items); setPurchasesTotal(r.total); }).catch(() => {});
  }, [purchasesPage]);

  const recharge = async (pkg: Pkg, provider: Provider) => {
    if (busy) return;
    setBusy(`${pkg.code}:${provider}`);
    try {
      const r = await Orders.create({ kind: "points", package_code: pkg.code, provider });
      if (r.redirect_url) window.location.href = r.redirect_url;
      else if (r.qr_code_url) setPendingQR({ url: r.qr_code_url, orderId: r.order_id });
      else toast.info("订单已创建，请到「我的订单」继续支付");
    } catch (e: any) {
      toast.error(e?.body?.message || e?.message || "创建订单失败");
    } finally {
      setBusy(null);
    }
  };

  if (err) return <div className="py-16 text-center text-destructive">{err}</div>;
  if (!w) return <div className="py-16 text-center text-muted-foreground">加载中...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <Coins className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-sm text-muted-foreground">当前积分余额</div>
            <div className="text-3xl font-semibold">{w.points}</div>
          </div>
          <Sparkles className="h-5 w-5 text-amber-500" />
        </CardContent>
      </Card>

      <CheckinPanel onSigned={() => { Wallet.get().then(setW).catch(() => {}); setTxPage(1); }} />

      <RedeemPanel onSuccess={() => { Wallet.get().then(setW).catch(() => {}); setTxPage(1); }} />

      {providers.length === 0 && packages.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">暂未开启在线支付</div>
            <div>管理员尚未配置支付渠道，请前往后台「站点设置 → 支付渠道」启用。</div>
          </div>
        </div>
      )}

      {packages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" /> 充值积分
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((p, idx) => {
                const recommended = idx === 1;
                return (
                  <Card key={p.id} className={`flex flex-col relative ${recommended ? "border-primary shadow-lg shadow-primary/10" : ""}`}>
                    {recommended && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-xs px-3 py-0.5">
                        推荐
                      </span>
                    )}
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">{p.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-2">
                      <div className="text-3xl font-bold flex items-baseline gap-1">
                        {p.points}
                        <span className="text-sm font-normal text-muted-foreground">积分</span>
                      </div>
                      {p.bonus > 0 && (
                        <div className="text-xs text-amber-600">额外赠送 {p.bonus} 积分</div>
                      )}
                      {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                      <div className="text-lg font-semibold">¥ {fenToYuan(p.price_cents)}</div>
                      <div className="mt-auto flex flex-col gap-2 pt-3">
                        {providers.length === 0 ? (
                          <Button disabled variant="outline" className="w-full">
                            <CreditCard className="h-4 w-4 mr-1" /> 暂无可用支付方式
                          </Button>
                        ) : (
                          providers.map((pr) => (
                            <Button
                              key={pr}
                              disabled={busy === `${p.code}:${pr}`}
                              onClick={() => recharge(p, pr)}
                              className={PROVIDER_META[pr].tone}
                            >
                              {busy === `${p.code}:${pr}` ? "处理中…" : PROVIDER_META[pr].label}
                            </Button>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>已购资源</CardTitle></CardHeader>
        <CardContent>
          {purchases.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无</div>
          ) : (
            <>
              <ul className="divide-y">
                {purchases.map((r) => (
                  <li key={r.project_id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                    <a href={`#/p/${r.slug}`} className="font-medium hover:text-primary line-clamp-1">{r.title}</a>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground shrink-0">
                      <span>{r.paid_points} 积分</span>
                      <span>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <Pagination page={purchasesPage} total={purchasesTotal} pageSize={PAGE_SIZE} onChange={setPurchasesPage} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>积分流水</CardTitle></CardHeader>
        <CardContent>
          {tx.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无</div>
          ) : (
            <>
              <ul className="divide-y">
                {tx.map((r) => (
                  <li key={r.id} className="py-2.5 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{r.note || r.kind}</div>
                      <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()} · {r.kind}</div>
                    </div>
                    <div className={"font-mono " + (r.delta_points < 0 ? "text-destructive" : "text-emerald-600")}>
                      {r.delta_points > 0 ? "+" : ""}{r.delta_points} 积分
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <Pagination page={txPage} total={txTotal} pageSize={PAGE_SIZE} onChange={setTxPage} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {pendingQR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPendingQR(null)}>
          <Card className="max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader><CardTitle>请使用对应 App 扫码支付</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-center">
              <img alt="qr" className="mx-auto rounded-md border" width={220} height={220} src={pendingQR.url} />
              <div className="text-xs text-muted-foreground">支付完成后积分将自动到账。订单号：{pendingQR.orderId}</div>
              <Button variant="outline" onClick={() => setPendingQR(null)} className="w-full">关闭</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
